import {
  isHalfWidthNumeric,
  FieldValidationResult,
  ValidationError,
  isHalfOrFullWidthNumeric,
} from '../../companies/utils/validations/common-validation';

import type {
  PropertyRegistrationForm,
  FieldStaffItem,
  ContractFactoryItem,
} from '../types/property.types';

// Numeric validation helper
export const isNumeric = (value: string | number): boolean => {
  if (typeof value === 'number') {
    return !Number.isNaN(value) && Number.isFinite(value);
  }
  if (typeof value === 'string') {
    return /^\d+(\.\d+)?$/.test(value.trim());
  }
  return false;
};

// Integer validation helper
export const isInteger = (value: string | number): boolean => {
  if (typeof value === 'number') {
    return Number.isInteger(value);
  }
  if (typeof value === 'string') {
    return /^\d+$/.test(value.trim());
  }
  return false;
};

// Individual field validations
export const validateWaoPropertyCode = (code: string): FieldValidationResult => {
  if (!code.trim()) {
    return { isValid: true }; // Optional field
  }

  if (code.length > 30) {
    return {
      isValid: false,
      error: 'WAO現場コードは30文字以内で入力してください。',
    };
  }

  return { isValid: true };
};

export const validatePropertyName = (name: string): FieldValidationResult => {
  if (!name.trim()) {
    return {
      isValid: false,
      error: '現場は必須入力項目です。',
    };
  }

  if (name.length > 200) {
    return {
      isValid: false,
      error: '現場名は200文字以内で入力してください。',
    };
  }

  return { isValid: true };
};

export const validatePropertyNameAbbreviation = (name: string): FieldValidationResult => {
  if (!name.trim()) {
    return {
      isValid: false,
      error: '現場略称は必須入力項目です。',
    };
  }

  if (name.length > 100) {
    return {
      isValid: false,
      error: '現場名略称は100文字以内で入力してください。',
    };
  }

  return { isValid: true };
};

export const validatePostalCodeFirstHalf = (
  value: number | string,
  isTouched: boolean = false
): FieldValidationResult => {
  const strValue = value?.toString() || '';

  // Only validate if field is touched and has value
  if (!isTouched || !strValue.trim()) {
    return { isValid: true }; // Optional field, no validation if not touched or empty
  }

  if (!isHalfWidthNumeric(strValue)) {
    return {
      isValid: false,
      error: '郵便番号（前半）は半角数値で入力ください。',
    };
  }

  if (strValue.length !== 3) {
    return {
      isValid: false,
      error: '郵便番号（前半）は3桁で入力してください。',
    };
  }

  return { isValid: true };
};

export const validatePostalCodeLatterHalf = (
  value: number | string,
  isTouched: boolean = false
): FieldValidationResult => {
  const strValue = value?.toString() || '';

  // Only validate if field is touched and has value
  if (!isTouched || !strValue.trim()) {
    return { isValid: true }; // Optional field, no validation if not touched or empty
  }

  if (!isHalfWidthNumeric(strValue)) {
    return {
      isValid: false,
      error: '郵便番号（後半）は半角数値で入力ください。',
    };
  }

  if (strValue.length !== 4) {
    return {
      isValid: false,
      error: '郵便番号（後半）は4桁で入力してください。',
    };
  }

  return { isValid: true };
};

// Validate postal code pair - both must be filled together
export const validatePostalCodePair = (
  firstHalf: number | string,
  latterHalf: number | string,
  firstTouched: boolean,
  latterTouched: boolean
): FieldValidationResult => {
  const firstValue = firstHalf?.toString() || '';
  const latterValue = latterHalf?.toString() || '';

  // If neither field is touched, no validation
  if (!firstTouched && !latterTouched) {
    return { isValid: true };
  }

  // If both fields are empty, no validation needed
  if (!firstValue.trim() && !latterValue.trim()) {
    return { isValid: true };
  }

  // If both fields have values, no validation needed here (individual validations will handle format)
  if (firstValue.trim() && latterValue.trim()) {
    return { isValid: true };
  }

  // If only one field has value, show error
  return {
    isValid: false,
    error: '郵便番号（前半）と郵便番号（後半）の一方のみの登録はできません。',
  };
};

export const validateAddress = (address: string): FieldValidationResult => {
  if (!address.trim()) {
    return {
      isValid: false,
      error: '住所は必須入力項目です。',
    };
  }

  if (address.length > 200) {
    return {
      isValid: false,
      error: '住所は200文字以内で入力してください。',
    };
  }

  return { isValid: true };
};

export const validateStartDate = (date: string): FieldValidationResult => {
  if (!date.trim()) {
    return {
      isValid: false,
      error: '開始日は必須入力項目です。',
    };
  }

  // Check if it's a valid date
  const dateObj = new Date(date);
  if (Number.isNaN(dateObj.getTime())) {
    return {
      isValid: false,
      error: '有効な日付を入力してください。',
    };
  }

  return { isValid: true };
};

export const validateConstructionPeriod = (date: string): FieldValidationResult => {
  if (!date.trim()) {
    return { isValid: true }; // Optional field
  }

  // Check if it's a valid date
  const dateObj = new Date(date);
  if (Number.isNaN(dateObj.getTime())) {
    return {
      isValid: false,
      error: '有効な日付を入力してください。',
    };
  }

  return { isValid: true };
};

// Validate construction period range - to date must be >= from date
export const validateConstructionPeriodRange = (
  fromDate: string,
  toDate: string
): FieldValidationResult => {
  // If either field is empty, no validation needed
  if (!fromDate.trim() || !toDate.trim()) {
    return { isValid: true };
  }

  const fromDateObj = new Date(fromDate);
  const toDateObj = new Date(toDate);

  // Check if dates are valid
  if (Number.isNaN(fromDateObj.getTime()) || Number.isNaN(toDateObj.getTime())) {
    return { isValid: true }; // Individual validations will handle invalid dates
  }

  // Check if to date is before from date
  if (toDateObj < fromDateObj) {
    return {
      isValid: false,
      error: '工期終了日は工期開始日以降の日付を選択してください。',
    };
  }

  return { isValid: true };
};

export const validateOwnerName = (name: string): FieldValidationResult => {
  if (!name.trim()) {
    return { isValid: true }; // Optional field
  }

  if (name.length > 100) {
    return {
      isValid: false,
      error: '施主名は100文字以内で入力してください。',
    };
  }

  return { isValid: true };
};

export const validateCustomerId = (id: string | number): FieldValidationResult => {
  // Check if empty string, 0, null, or undefined
  if (!id || id === 0 || id === '') {
    return {
      isValid: false,
      error: '得意先は必須入力項目です。',
    };
  }

  return { isValid: true };
};

export const validateConstructionCompanyId = (id: string | number): FieldValidationResult => {
  // Check if empty string, 0, null, or undefined
  if (!id || id === 0 || id === '') {
    return {
      isValid: false,
      error: '建設会社は必須入力項目です。',
    };
  }

  return { isValid: true };
};

export const validateOfficeAddress = (address: string): FieldValidationResult => {
  if (!address.trim()) {
    return { isValid: true }; // Optional field
  }

  if (address.length > 200) {
    return {
      isValid: false,
      error: '事務所住所は200文字以内で入力してください。',
    };
  }

  return { isValid: true };
};

export const validateOfficeTel = (tel: string): FieldValidationResult => {
  if (!tel.trim()) {
    return { isValid: true }; // Optional field
  }

  if (tel.length > 30) {
    return {
      isValid: false,
      error: '事務所TELは30文字以内で入力してください。',
    };
  }

  if (!isHalfOrFullWidthNumeric(tel)) {
    return {
      isValid: false,
      error: '事務所TELは数字とハイフンのみで入力してください。',
    };
  }

  return { isValid: true };
};

export const validateOfficeFax = (fax: string): FieldValidationResult => {
  if (!fax.trim()) {
    return { isValid: true }; // Optional field
  }

  if (fax.length > 30) {
    return {
      isValid: false,
      error: '事務所FAXは30文字以内で入力してください。',
    };
  }

  if (!isHalfOrFullWidthNumeric(fax)) {
    return {
      isValid: false,
      error: '事務所FAXは数字とハイフンのみで入力してください。',
    };
  }

  return { isValid: true };
};

export const validateStructuralFormatOthers = (value: string): FieldValidationResult => {
  if (!value.trim()) {
    return { isValid: true }; // Optional field
  }

  if (value.length > 20) {
    return {
      isValid: false,
      error: '構造形式（その他）は20文字以内で入力してください。',
    };
  }

  return { isValid: true };
};

export const validateNumberOfFloorsAboveGround = (
  value: string | number
): FieldValidationResult => {
  const strValue = value?.toString() || '';

  if (!strValue.trim()) {
    return { isValid: true }; // Optional field
  }

  if (!isInteger(strValue)) {
    return {
      isValid: false,
      error: '地上階数は正の整数で入力してください。',
    };
  }

  if (strValue.length > 9) {
    return {
      isValid: false,
      error: '地上階数は9桁以内で入力してください。',
    };
  }

  return { isValid: true };
};

export const validateNumberOfBasementFloors = (value: string | number): FieldValidationResult => {
  const strValue = value?.toString() || '';

  if (!strValue.trim()) {
    return { isValid: true }; // Optional field
  }

  if (!isInteger(strValue)) {
    return {
      isValid: false,
      error: '地下階数は正の整数で入力してください。',
    };
  }

  if (strValue.length > 9) {
    return {
      isValid: false,
      error: '地下階数は9桁以内で入力してください。',
    };
  }

  return { isValid: true };
};

export const validateSiteManagerName = (name: string): FieldValidationResult => {
  if (!name.trim()) {
    return { isValid: true }; // Optional field
  }

  if (name.length > 50) {
    return {
      isValid: false,
      error: '現場責任者名は50文字以内で入力してください。',
    };
  }

  return { isValid: true };
};

export const validateSiteManagerTel = (tel: string): FieldValidationResult => {
  if (!tel.trim()) {
    return { isValid: true }; // Optional field
  }

  if (tel.length > 20) {
    return {
      isValid: false,
      error: '現場責任者TELは20文字以内で入力してください。',
    };
  }

  if (!/^[\d-]+$/.test(tel)) {
    return {
      isValid: false,
      error: '現場責任者TELは数字とハイフンのみで入力してください。',
    };
  }

  return { isValid: true };
};

export const validateReadyMixedConcreteContractQuantity = (
  value: number | string
): FieldValidationResult => {
  const strValue = value?.toString() || '';

  if (!strValue.trim()) {
    return { isValid: true }; // Optional field
  }

  if (!isNumeric(strValue)) {
    return {
      isValid: false,
      error: '生コンクリート契約数量は半角数値で入力ください。',
    };
  }

  if (strValue.length > 9) {
    return {
      isValid: false,
      error: '生コンクリート契約数量は9桁以内で入力してください。',
    };
  }

  return { isValid: true };
};

export const validateDepartmentCode = (code: number): FieldValidationResult => {
  if (!code || code === 0) {
    return {
      isValid: false,
      error: '担当部署は必須選択項目です。',
    };
  }

  return { isValid: true };
};

export const validateSalesRepresentativeCode = (code: string): FieldValidationResult => {
  if (!code || code === '' || code === '0') {
    return {
      isValid: false,
      error: '営業担当者は必須選択項目です。',
    };
  }

  return { isValid: true };
};

export const validateDeliveryPersonCode = (code: string): FieldValidationResult => {
  if (!code || code === '' || code === '0') {
    return {
      isValid: false,
      error: 'デリバリー担当者は必須選択項目です。',
    };
  }

  return { isValid: true };
};

export const validateReadyMixConcreteStaffName = (name: string): FieldValidationResult => {
  if (!name.trim()) {
    return { isValid: true }; // Optional field
  }

  if (name.length > 50) {
    return {
      isValid: false,
      error: '生コン担当者名は50文字以内で入力してください。',
    };
  }

  return { isValid: true };
};

export const validateReadyMixConcreteStaffTel = (tel: string): FieldValidationResult => {
  if (!tel.trim()) {
    return { isValid: true }; // Optional field
  }

  if (tel.length > 20) {
    return {
      isValid: false,
      error: '生コン担当者TELは20文字以内で入力してください。',
    };
  }

  if (!/^[\d-]+$/.test(tel)) {
    return {
      isValid: false,
      error: '生コン担当者TELは数字とハイフンのみで入力してください。',
    };
  }

  return { isValid: true };
};

// Field staff member validation
export const validateFieldStaffMember = (
  member: FieldStaffItem,
  index: number
): ValidationError[] => {
  const errors: ValidationError[] = [];

  // Check if member has any data filled in
  const hasAnyData = member.name?.trim() || member.tel?.trim() || member.email?.trim();

  // If no data at all, skip validation (empty member is allowed)
  if (!hasAnyData) {
    return errors;
  }

  // Validate name - OPTIONAL field, only validate length if provided
  if (member.name?.trim() && member.name.length > 50) {
    errors.push({
      field: `field_staffs.${index}.name`,
      message: '氏名は50文字以内で入力してください。',
    });
  }

  // Validate TEL format if provided
  if (member.tel?.trim()) {
    if (!/^[\d-]+$/.test(member.tel)) {
      errors.push({
        field: `field_staffs.${index}.tel`,
        message: 'TELは数字とハイフンのみで入力してください。',
      });
    }

    if (member.tel.length > 20) {
      errors.push({
        field: `field_staffs.${index}.tel`,
        message: 'TELは20文字以内で入力してください。',
      });
    }
  }

  // Validate email format if provided
  if (member.email?.trim()) {
    // Prevent ReDoS by limiting input length before regex test
    if (member.email.length > 100) {
      errors.push({
        field: `field_staffs.${index}.email`,
        message: 'メールアドレスは100文字以内で入力してください。',
      });
    } else {
      // Use a safer regex pattern with limited backtracking potential
      // Check for basic email structure: local@domain.tld
      const emailParts = member.email.split('@');
      if (
        emailParts.length !== 2 ||
        !emailParts[0] ||
        !emailParts[1] ||
        !/^[^\s@]+$/.test(emailParts[0]) ||
        !/^[^\s@.]+\.[^\s@]+$/.test(emailParts[1])
      ) {
        errors.push({
          field: `field_staffs.${index}.email`,
          message: '有効なメールアドレスを入力してください。',
        });
      }
    }
  }

  return errors;
};

// Contract factory member validation
export const validateContractFactoryMember = (
  member: ContractFactoryItem,
  index: number
): ValidationError[] => {
  const errors: ValidationError[] = [];

  // Check if member has any data filled in
  const hasAnyData =
    member.factory?.trim() ||
    member.delivery_classification?.trim() ||
    (member.quantity && member.quantity !== 0);

  // If no data at all, skip validation (empty factory is allowed)
  if (!hasAnyData) {
    return errors;
  }

  // Validate factory - OPTIONAL field, only validate length if provided
  if (member.factory?.trim() && member.factory.length > 100) {
    errors.push({
      field: `contract_factories.${index}.factory`,
      message: '工場は100文字以内で入力してください。',
    });
  }

  // Validate delivery_classification - OPTIONAL field, only validate length if provided
  if (member.delivery_classification?.trim() && member.delivery_classification.length > 50) {
    errors.push({
      field: `contract_factories.${index}.delivery_classification`,
      message: '納入区分は50文字以内で入力してください。',
    });
  }

  // Validate quantity format if provided
  if (member.quantity !== undefined && member.quantity !== null && member.quantity !== 0) {
    if (!isNumeric(member.quantity)) {
      errors.push({
        field: `contract_factories.${index}.quantity`,
        message: '数量は半角数値で入力ください。',
      });
    }
  }

  return errors;
};

// Information input section validations
export const validateAchievementConfirmationCompany = (company: string): FieldValidationResult => {
  if (!company.trim()) {
    return { isValid: true }; // Optional field
  }

  if (company.length > 100) {
    return {
      isValid: false,
      error: '実績確認会社は100文字以内で入力してください。',
    };
  }

  return { isValid: true };
};

export const validateNoharaDirect = (value: string): FieldValidationResult => {
  if (!value.trim()) {
    return { isValid: true }; // Optional field
  }

  if (value.length > 50) {
    return {
      isValid: false,
      error: '当社ープラント直接契約は50文字以内で入力してください。',
    };
  }

  return { isValid: true };
};

export const validatePWindow = (value: string): FieldValidationResult => {
  if (!value.trim()) {
    return { isValid: true }; // Optional field
  }

  if (value.length > 50) {
    return {
      isValid: false,
      error: 'プラント窓口会社は50文字以内で入力してください。',
    };
  }

  return { isValid: true };
};

export const validateSellersOtherGc = (value: string): FieldValidationResult => {
  if (!value.trim()) {
    return { isValid: true }; // Optional field
  }

  if (value.length > 50) {
    return {
      isValid: false,
      error: 'GC以外の販売先は50文字以内で入力してください。',
    };
  }

  return { isValid: true };
};

export const validateMemo = (value: string): FieldValidationResult => {
  if (!value.trim()) {
    return { isValid: true }; // Optional field
  }

  if (value.length > 100) {
    return {
      isValid: false,
      error: '備考は100文字以内で入力してください。',
    };
  }

  return { isValid: true };
};

export const validateResultsCaution = (value: string): FieldValidationResult => {
  if (!value.trim()) {
    return { isValid: true }; // Optional field
  }

  if (value.length > 100) {
    return {
      isValid: false,
      error: '実績記入欄他注意事項は100文字以内で入力してください。',
    };
  }

  return { isValid: true };
};

export const validateJvPrice = (value: number | string): FieldValidationResult => {
  const strValue = value?.toString() || '';

  if (!strValue.trim()) {
    return { isValid: true }; // Optional field
  }

  if (!isNumeric(strValue)) {
    return {
      isValid: false,
      error: 'JV価格は半角数値で入力ください。',
    };
  }

  if (strValue.length > 9) {
    return {
      isValid: false,
      error: 'JV価格は9桁以内で入力してください。',
    };
  }

  return { isValid: true };
};

export const validateGeneralContractorPurchasePrice = (
  value: number | string
): FieldValidationResult => {
  const strValue = value?.toString() || '';

  if (!strValue.trim()) {
    return { isValid: true }; // Optional field
  }

  if (!isNumeric(strValue)) {
    return {
      isValid: false,
      error: 'ゼネコン買付価格は半角数値で入力ください。',
    };
  }

  if (strValue.length > 9) {
    return {
      isValid: false,
      error: 'ゼネコン買付価格は9桁以内で入力してください。',
    };
  }

  return { isValid: true };
};

export const validateCooperative = (value: string): FieldValidationResult => {
  if (!value.trim()) {
    return { isValid: true }; // Optional field
  }

  if (value.length > 50) {
    return {
      isValid: false,
      error: '協同組合は50文字以内で入力してください。',
    };
  }

  return { isValid: true };
};

// Main form validation
/**
 * Helper function to add validation error if validation fails
 * Reduces cognitive complexity by encapsulating the if-check pattern
 */
const addValidationError = (
  errors: ValidationError[],
  result: FieldValidationResult,
  fieldName: string
): void => {
  if (!result.isValid) {
    errors.push({ field: fieldName, message: result.error });
  }
};

/**
 * Helper function to add validation error to multiple fields
 */
const addValidationErrorToFields = (
  errors: ValidationError[],
  result: FieldValidationResult,
  fieldNames: string[]
): void => {
  if (!result.isValid) {
    errors.push(...fieldNames.map((fieldName) => ({ field: fieldName, message: result.error })));
  }
};

/**
 * Validates basic information section fields
 */
const validateBasicInformation = (
  form: PropertyRegistrationForm,
  touchedFields: Set<string>,
  errors: ValidationError[]
): void => {
  addValidationError(
    errors,
    validateWaoPropertyCode(form.wao_property_code || ''),
    'wao_property_code'
  );
  addValidationError(errors, validatePropertyName(form.property_name || ''), 'property_name');
  addValidationError(
    errors,
    validatePropertyNameAbbreviation(form.property_name_abbreviation || ''),
    'property_name_abbreviation'
  );

  addValidationError(
    errors,
    validatePostalCodeFirstHalf(
      form.postal_code_first_half || 0,
      touchedFields.has('postal_code_first_half')
    ),
    'postal_code_first_half'
  );
  addValidationError(
    errors,
    validatePostalCodeLatterHalf(
      form.postal_code_latter_half || 0,
      touchedFields.has('postal_code_latter_half')
    ),
    'postal_code_latter_half'
  );

  // Validate postal code pair - both must be filled together
  addValidationErrorToFields(
    errors,
    validatePostalCodePair(
      form.postal_code_first_half || 0,
      form.postal_code_latter_half || 0,
      touchedFields.has('postal_code_first_half'),
      touchedFields.has('postal_code_latter_half')
    ),
    ['postal_code_first_half', 'postal_code_latter_half']
  );

  addValidationError(errors, validateAddress(form.address || ''), 'address');
  addValidationError(errors, validateStartDate(form.start_date || ''), 'start_date');
  addValidationError(errors, validateOwnerName(form.owner_name || ''), 'owner_name');
  addValidationError(errors, validateCustomerId(form.customer_id || ''), 'customer_id');
  addValidationError(
    errors,
    validateConstructionCompanyId(form.construction_company_id || ''),
    'construction_company_id'
  );
};

/**
 * Validates construction period fields
 */
const validateConstructionPeriodFields = (
  form: PropertyRegistrationForm,
  errors: ValidationError[]
): void => {
  addValidationError(
    errors,
    validateConstructionPeriod(form.construction_period_from || ''),
    'construction_period_from'
  );
  addValidationError(
    errors,
    validateConstructionPeriod(form.construction_period_to || ''),
    'construction_period_to'
  );

  // Validate construction period range - to date must be >= from date
  addValidationError(
    errors,
    validateConstructionPeriodRange(
      form.construction_period_from || '',
      form.construction_period_to || ''
    ),
    'construction_period_to'
  );
};

/**
 * Validates property details section fields
 */
const validatePropertyDetails = (
  form: PropertyRegistrationForm,
  errors: ValidationError[]
): void => {
  addValidationError(errors, validateOfficeAddress(form.office_address || ''), 'office_address');
  addValidationError(errors, validateOfficeTel(form.office_tel || ''), 'office_tel');
  addValidationError(errors, validateOfficeFax(form.office_fax || ''), 'office_fax');
  addValidationError(
    errors,
    validateStructuralFormatOthers(form.structural_format_others || ''),
    'structural_format_others'
  );
  addValidationError(
    errors,
    validateNumberOfFloorsAboveGround(form.number_of_floors_above_ground || ''),
    'number_of_floors_above_ground'
  );
  addValidationError(
    errors,
    validateNumberOfBasementFloors(form.number_of_basement_floors || ''),
    'number_of_basement_floors'
  );
  addValidationError(
    errors,
    validateSiteManagerName(form.site_manager_name || ''),
    'site_manager_name'
  );
  addValidationError(
    errors,
    validateSiteManagerTel(form.site_manager_tel || ''),
    'site_manager_tel'
  );
  addValidationError(
    errors,
    validateReadyMixedConcreteContractQuantity(form.ready_mixed_concrete_contract_quantity || 0),
    'ready_mixed_concrete_contract_quantity'
  );
};

/**
 * Validates representatives section fields
 */
const validateRepresentatives = (
  form: PropertyRegistrationForm,
  errors: ValidationError[]
): void => {
  addValidationError(errors, validateDepartmentCode(form.department_code || 0), 'department_code');
  addValidationError(
    errors,
    validateSalesRepresentativeCode(form.sales_representative_code || ''),
    'sales_representative_code'
  );
  addValidationError(
    errors,
    validateDeliveryPersonCode(form.delivery_person_code || ''),
    'delivery_person_code'
  );
};

/**
 * Validates onsite staff section fields
 */
const validateOnsiteStaff = (form: PropertyRegistrationForm, errors: ValidationError[]): void => {
  addValidationError(
    errors,
    validateReadyMixConcreteStaffName(form.ready_mix_concrete_staff_name || ''),
    'ready_mix_concrete_staff_name'
  );
  addValidationError(
    errors,
    validateReadyMixConcreteStaffTel(form.ready_mix_concrete_staff_tel || ''),
    'ready_mix_concrete_staff_tel'
  );

  // Validate field staff members
  if (form.field_staffs) {
    for (const [index, member] of form.field_staffs.entries()) {
      errors.push(...validateFieldStaffMember(member, index));
    }
  }

  // Validate contract factory members
  if (form.contract_factories) {
    for (const [index, member] of form.contract_factories.entries()) {
      errors.push(...validateContractFactoryMember(member, index));
    }
  }
};

/**
 * Validates information input section fields
 */
const validateInformationInput = (
  form: PropertyRegistrationForm,
  errors: ValidationError[]
): void => {
  addValidationError(
    errors,
    validateAchievementConfirmationCompany(form.achievement_confirmation_company || ''),
    'achievement_confirmation_company'
  );
  addValidationError(errors, validateNoharaDirect(form.nohara_direct || ''), 'nohara_direct');
  addValidationError(errors, validatePWindow(form.p_window || ''), 'p_window');
  addValidationError(
    errors,
    validateSellersOtherGc(form.sellers_other_gc || ''),
    'sellers_other_gc'
  );
  addValidationError(errors, validateMemo(form.memo || ''), 'memo');
  addValidationError(errors, validateResultsCaution(form.results_caution || ''), 'results_caution');
  addValidationError(errors, validateJvPrice(form.jv_price || 0), 'jv_price');
  addValidationError(
    errors,
    validateGeneralContractorPurchasePrice(form.general_contractor_purchase_price || 0),
    'general_contractor_purchase_price'
  );
  addValidationError(errors, validateCooperative(form.cooperative || ''), 'cooperative');
};

export const validatePropertyRegistrationForm = (
  form: PropertyRegistrationForm,
  touchedFields: Set<string> = new Set()
): { isValid: boolean; errors: ValidationError[] } => {
  const errors: ValidationError[] = [];

  validateBasicInformation(form, touchedFields, errors);
  validateConstructionPeriodFields(form, errors);
  validatePropertyDetails(form, errors);
  validateRepresentatives(form, errors);
  validateOnsiteStaff(form, errors);
  validateInformationInput(form, errors);

  return {
    isValid: errors.length === 0,
    errors,
  };
};
